import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { syncUserDetails } from 'actions/AuthActions'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import type { VisibleLanguageOption } from 'i18n/util/locale'

function NavBarLanguageItems(props: {
  onLanguageSelect?: (e: React.MouseEvent<HTMLButtonElement>, changed: boolean) => void
}) {
  const { onLanguageSelect } = props
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const {
    locale,
    languageOptions,
    preReleaseLanguageOptions,
  } = useLocaleUrlOptions()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const preReleaseLocales = useMemo(
    () => new Set(preReleaseLanguageOptions.map(option => option.locale)),
    [preReleaseLanguageOptions],
  )

  const currentLanguage =
    languageOptions.find((lang) => lang.locale === locale) ??
    languageOptions[0]

  const onLanguageClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      const selectedLanguage = languageOptions.find(
        (language) => language.locale === e.currentTarget.dataset.locale,
      )

      if (selectedLanguage) {
        const changed = selectedLanguage.locale !== currentLanguage?.locale
        if (changed) {
          setLanguageCookie(selectedLanguage.locale)
          if (isLoggedIn) {
            updateCustomerLanguagePreferenceFromAuth(selectedLanguage.locale)
              .catch(reportClientError)
            dispatch(syncUserDetails({ language: selectedLanguage.locale }))
          }
          const currentLocationUrl = UrlLocation.fromLocation(window.location)
          const redirect = currentLocationUrl.getRedirectToLocale({
            targetLocale: selectedLanguage.locale,
            languageOptions,
          })
          const targetUrl = currentLocationUrl.withRedirect(redirect)

          if (!targetUrl.equals(currentLocationUrl)) {
            window.location.href = targetUrl.toString()
          }
        }
        onLanguageSelect?.(e, changed)
      }
    },
    [dispatch, currentLanguage?.locale, languageOptions, onLanguageSelect, isLoggedIn],
  )

  const renderLanguageOption = useCallback((language: VisibleLanguageOption) => {
    const title = preReleaseLocales.has(language.locale)
      ? intl.formatMessage({
          id: 'tSYn7WdRPK4GJUF883re',
          defaultMessage: '{language} (staff only)',
          description: 'Language picker label for a pre-release locale available only to Luxury Escapes staff',
        }, { language: language.nativeName })
      : language.nativeName

    return (
      <ClickableListItem
        size="medium"
        key={language.locale}
        endIcon={
          currentLanguage?.locale === language.locale ? (
            <LineCheckIcon />
          ) : undefined
        }
        title={title}
        data-locale={language.locale}
        onClick={onLanguageClick}
        selected={currentLanguage?.locale === language.locale}
      />
    )
  }, [currentLanguage?.locale, intl, onLanguageClick, preReleaseLocales])

  return (
    <>
      {languageOptions.map(renderLanguageOption)}
    </>
  )
}

export default React.memo(NavBarLanguageItems)
